import { create } from 'zustand';
import { RegionListItemDto, CreateRegionCommand, UpdateRegionCommand } from '../../domain/entities/Region';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { RegionService } from '../services/RegionService';
import { RegionRepository } from '../../infrastructure/repositories/RegionRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';

/**
 * Region store state interface
 */
interface RegionStoreState {
  // State
  regions: RegionListItemDto[];  // List API returns RegionListItemDto not full RegionDto
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchRegions: (params?: PaginationParams, userRole?: UserRole) => Promise<void>;
  createRegion: (command: CreateRegionCommand, userRole: UserRole) => Promise<void>;
  updateRegion: (id: string, command: UpdateRegionCommand, userRole: UserRole) => Promise<void>;
  deleteRegion: (id: string, userRole: UserRole) => Promise<void>;
  toggleActive: (id: string, userRole: UserRole) => Promise<void>;
  updateSubreddits: (id: string, subreddits: string[], userRole: UserRole) => Promise<void>;
  clearError: () => void;
}

/**
 * Create service dependencies
 */
const regionRepository = new RegionRepository(apiClient);
const regionService = new RegionService(regionRepository);

/**
 * Region Store - Manages region state (admin only)
 *
 * Provides:
 * - Regions list with pagination
 * - CRUD operations (admin only)
 * - Subreddit management
 * - Toggle active status
 */
export const useRegionStore = create<RegionStoreState>((set, get) => ({
  // Initial state
  regions: [],
  isLoading: false,
  error: null,

  /**
   * Fetch all regions (admin only)
   */
  fetchRegions: async (params?: PaginationParams, userRole?: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      const result: PaginatedResult<RegionListItemDto> = await regionService.getAllRegions(params, userRole);

      set({
        regions: result.items,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch regions';

      set({
        regions: [],
        isLoading: false,
        error: errorMessage
      });
    }
  },

  /**
   * Create a new region (admin only)
   */
  createRegion: async (command: CreateRegionCommand, userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await regionService.createRegion(command, userRole);

      // Refetch regions
      await get().fetchRegions(undefined, userRole);

      set({
        isLoading: false,
        error: null
      });

      // Show success notification
      notificationService.showSuccess(`Region "${command.name}" has been created successfully`, 'Region Created');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create region';

      set({
        isLoading: false,
        error: errorMessage
      });

      // Error notification already shown by ApiClient
      throw error;
    }
  },

  /**
   * Update an existing region (admin only)
   */
  updateRegion: async (id: string, command: UpdateRegionCommand, userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await regionService.updateRegion(id, command, userRole);

      // Update the region in local state
      const regions = get().regions.map((region) =>
        region.id === id
          ? { ...region, ...command }
          : region
      );

      set({
        regions,
        isLoading: false,
        error: null
      });

      // Show success notification
      notificationService.showSuccess('Region has been updated successfully', 'Region Updated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update region';

      set({
        isLoading: false,
        error: errorMessage
      });

      // Error notification already shown by ApiClient
      throw error;
    }
  },

  /**
   * Delete a region (admin only)
   */
  deleteRegion: async (id: string, userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await regionService.deleteRegion(id, userRole);

      // Remove the region from local state
      const regions = get().regions.filter((region) => region.id !== id);

      set({
        regions,
        isLoading: false,
        error: null
      });

      // Show success notification
      notificationService.showSuccess('Region has been deleted successfully', 'Region Deleted');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete region';

      set({
        isLoading: false,
        error: errorMessage
      });

      // Error notification already shown by ApiClient
      throw error;
    }
  },

  /**
   * Toggle region active status (admin only)
   */
  toggleActive: async (id: string, userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await regionService.toggleRegionActive(id, userRole);

      // Toggle the isActive flag in local state
      const regions = get().regions.map((region) =>
        region.id === id
          ? { ...region, isActive: !region.isActive }
          : region
      );

      set({
        regions,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle region status';

      set({
        isLoading: false,
        error: errorMessage
      });

      throw error;
    }
  },

  /**
   * Update subreddits for a region (admin only)
   */
  updateSubreddits: async (id: string, subreddits: string[], userRole: UserRole): Promise<void> => {
    set({ isLoading: true, error: null });

    try {
      await regionService.manageSubreddits(id, subreddits, userRole);

      // Update the subreddits in local state
      const regions = get().regions.map((region) =>
        region.id === id
          ? { ...region, subreddits }
          : region
      );

      set({
        regions,
        isLoading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update subreddits';

      set({
        isLoading: false,
        error: errorMessage
      });

      throw error;
    }
  },

  /**
   * Clear error state
   */
  clearError: (): void => {
    set({ error: null });
  }
}));

/**
 * Selectors - Memoized state selectors for better performance
 */

/**
 * Get all active regions
 */
export const useActiveRegions = (): RegionListItemDto[] => {
  return useRegionStore((state) => state.regions.filter((region) => region.isActive));
};

/**
 * Get all regions
 */
export const useRegions = (): RegionListItemDto[] => {
  return useRegionStore((state) => state.regions);
};

/**
 * Get region by ID
 */
export const useRegionById = (id: string): RegionListItemDto | undefined => {
  return useRegionStore((state) => state.regions.find((region) => region.id === id));
};

/**
 * Get loading state
 */
export const useRegionsLoading = (): boolean => {
  return useRegionStore((state) => state.isLoading);
};

/**
 * Get error state
 */
export const useRegionsError = (): string | null => {
  return useRegionStore((state) => state.error);
};
